import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Anchor, ColorInput, Divider, Grid, TextInput } from '@mantine/core';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import AppCard from '../../common/AppCard';
import CreationWizardButton from './CreationWizardButton';
import MediaDropzone from '../media/MediaDropzone';
import { sterilizeUrlHandle } from '../../../helpers/format';
import { uploadFileToStorage } from '../../../helpers/awsHelper';
import { triggerNotification } from '../../../helpers/notification';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';

const FUNDRAISING_WINDOW_OPTIONS = [
  { label: '1 Month', value: '1_month' },
  { label: '3 months', value: '3_months' },
  { label: '6 months', value: '6_months' },
  { label: '1 year', value: '1_year' }
];

const StoreCreationSettingsStep = ({
  loading,
  createOptions,
  formState,
  setFormState
}) => {
  const { state: authState } = useContext(AuthContext);
  const [isImageUploading, setIsImageUploading] = useState(false);

  return (
    <AppStack style={{ gap: 16, paddingTop: 10 }}>
      <AppStack style={{ gap: 5 }}>
        <AppFlexbox
          style={{
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <AppText style={{ fontSize: 14, fontWeight: 500 }}>Branding</AppText>
          {false && (
            <Anchor
              onClick={() => setFormState({ ...formState })}
              style={{ color: 'dodgerblue', fontSize: 14 }}
            >
              Reset
            </Anchor>
          )}
        </AppFlexbox>
        <AppCard radius="md" shadow="none" style={{ padding: 8 }} withBorder>
          <AppStack style={{ gap: 8 }}>
            <AppFlexbox style={{ flex: 1 }}>
              <AppStack style={{ gap: 0 }}>
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  Logo image
                </AppText>
                <AppStack style={{ width: 275 }}>
                  <MediaDropzone
                    disabled={formState.loading}
                    disableDirectUrl
                    files={[]}
                    height={175}
                    loading={isImageUploading}
                    maxSelectCount={1}
                    onUpload={(media) => {
                      if (media[0]) {
                        setIsImageUploading(true);
                        uploadFileToStorage(
                          `${authState.userData.user.pkUser}-ecom-store-logo`,
                          media[0].file,
                          (location) => {
                            setFormState((c) => ({
                              ...c,
                              storeSettings: {
                                ...c.storeSettings,
                                logoImageUrl: location
                              }
                            }));
                            setIsImageUploading(false);
                          },
                          () => {
                            setIsImageUploading(false);
                            triggerNotification();
                          }
                        );
                      }
                      else {
                        setFormState((c) => ({
                          ...c,
                          storeSettings: {
                            ...c.storeSettings,
                            logoImageUrl: null
                          }
                        }));
                      }
                    }}
                    selectedPreview={formState.storeSettings?.logoImageUrl}
                  />
                </AppStack>
              </AppStack>

              <AppStack style={{ flex: 1, gap: 5 }}>
                <ColorInput
                  disabled={formState.loading}
                  label="Primary color"
                  onChange={(value) =>
                    setFormState({
                      ...formState,
                      storeSettings: {
                        ...formState.storeSettings,
                        primaryColor: value
                      }
                    })
                  }
                  popoverProps={{ zIndex: 1000 }}
                  value={formState.storeSettings.primaryColor}
                />

                <ColorInput
                  disabled={formState.loading}
                  label="Secondary color"
                  onChange={(value) =>
                    setFormState({
                      ...formState,
                      storeSettings: {
                        ...formState.storeSettings,
                        secondaryColor: value
                      }
                    })
                  }
                  popoverProps={{ zIndex: 1000 }}
                  value={formState.storeSettings.secondaryColor}
                />

                <TextInput
                  label="Support email"
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      storeSettings: {
                        ...formState.storeSettings,
                        supportEmail: e.currentTarget.value
                      }
                    })
                  }
                  type="email"
                  value={formState.storeSettings.supportEmail}
                />
              </AppStack>
            </AppFlexbox>
          </AppStack>
        </AppCard>
      </AppStack>

      <AppStack style={{ gap: 5 }}>
        <AppStack style={{ gap: 0 }}>
          <AppFlexbox
            style={{
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              URL handle
            </AppText>
          </AppFlexbox>
          <AppText style={{ fontSize: 14, color: '#666' }}>
            Define the unique URL handle for this store. This handle will be
            used as part of the URL to easily access and share the content.
          </AppText>
        </AppStack>
        <AppCard radius="md" shadow="none" style={{ padding: 8 }} withBorder>
          <AppStack style={{ gap: 8 }}>
            <TextInput
              onChange={(e) =>
                setFormState({
                  ...formState,
                  storeSettings: {
                    ...formState.storeSettings,
                    urlHandle: sterilizeUrlHandle(e.currentTarget.value || '')
                  }
                })
              }
              placeholder="Store url handle"
              value={formState.storeSettings.urlHandle}
            />
          </AppStack>
        </AppCard>
      </AppStack>

      <Divider />

      <AppStack style={{ gap: 5 }}>
        <AppStack style={{ gap: 0 }}>
          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
            Fundraising window (optional)
          </AppText>
          <AppText style={{ fontSize: 14, color: '#666' }}>
            Select when you want your fundraiser to end.
          </AppText>
        </AppStack>
        <AppCard radius="md" shadow="none" style={{ padding: 8 }} withBorder>
          <Grid>
            {FUNDRAISING_WINDOW_OPTIONS.map((option) => (
              <Grid.Col key={option.value} span={6}>
                <CreationWizardButton
                  label={option.label}
                  onClick={() => {
                    setFormState({
                      ...formState,
                      storeSettings: {
                        ...formState.storeSettings,
                        fundraisingWindowSize:
                          formState.storeSettings.fundraisingWindowSize ===
                          option.value
                            ? null
                            : option.value
                      }
                    });
                  }}
                  selected={
                    formState.storeSettings.fundraisingWindowSize ===
                    option.value
                  }
                />
              </Grid.Col>
            ))}
          </Grid>
        </AppCard>
        <AppText
          style={{
            fontSize: 14,
            color: '#666',
            fontStyle: 'italic',
            textAlign: 'center'
          }}
        >
          You can always set a custom date later in the store dashboard
          settings.
        </AppText>
      </AppStack>
    </AppStack>
  );
};

StoreCreationSettingsStep.propTypes = {
  createOptions: PropTypes.object,
  formState: PropTypes.object,
  loading: PropTypes.bool,
  setFormState: PropTypes.func
};

export default StoreCreationSettingsStep;
